<i18n>
{
  "fr": {
    "title": "Tâches à faire",
    "tab-general": "Conformité générale",
    "tab-projects": "Conformité par projet",
    "no-results": "Aucun indicateur ne correspond à ces critères.",
    "filter-owner": "Filtrer par utilisateur",
    "filter-owner-placeholder": "Tous les utilisateurs",
    "filter-owner-unassigned": "Non-assigné",
    "filter-project": "Filtrer par projet",
    "filter-project-placeholder": "Tous les projets",
    "filter-status": "Filtrer par statut",
    "filter-status-placeholder": "Tous les statuts",
    "filter-target-date": "Filtrer par échéance",
    "filter-target-date-placeholder": "Toutes les échéances",
    "filter-without-date": "Voir les indicateurs sans échéance"
  },
  "en": {
    "title": "Tasks to do",
    "tab-general": "General compliance",
    "tab-projects": "Project compliance",
    "no-results": "No indicator matches these filters.",
    "filter-owner": "Filter by user",
    "filter-owner-placeholder": "All users",
    "filter-owner-unassigned": "Unassigned",
    "filter-project": "Filter by project",
    "filter-project-placeholder": "All projects",
    "filter-status": "Filter by status",
    "filter-status-placeholder": "All statuses",
    "filter-target-date": "Filter by deadline",
    "filter-target-date-placeholder": "All deadlines",
    "filter-without-date": "Show indicators without a deadline"
  }
}
</i18n>

<template>
  <div>
    <ui-title>{{ $t('title') }}</ui-title>

    <b-tabs v-model="activeTab">
      <b-tab-item v-for="tab in tabs" :label="$t(`tab-${tab}`)" :value="tab" :key="tab">
        <div class="card card--overflow-visible">
          <ui-box>
            <h2 class="title is-size-4 has-text-weight-bold">{{ $t(`tab-${tab}`) }}</h2>

            <h3 class="has-text-weight-bold mb-8px">Filtrer par</h3>
            <div class="columns">
              <div class="column is-3">
                <s-field :label="$t('filter-owner')" v-if="owners" custom-class="sr-only">
                  <ui-dropdown expanded :items="ownerList" v-model="params.ownerId"></ui-dropdown>
                </s-field>
              </div>
              <div class="column is-3" v-if="tab === TAB_PROJECTS">
                <s-field :label="$t('filter-project')" v-if="projects" custom-class="sr-only">
                  <ui-dropdown expanded :items="projectList" v-model="params.projectId"></ui-dropdown>
                </s-field>
              </div>
              <div class="column is-3">
                <s-field :label="$t('filter-status')" v-if="statusList" custom-class="sr-only">
                  <ui-dropdown expanded :items="statusList" v-model="params.status"></ui-dropdown>
                </s-field>
              </div>
              <div class="column is-3">
                <s-field :label="$t('filter-target-date')" v-if="targetDateList" custom-class="sr-only">
                  <ui-dropdown expanded :items="targetDateList" v-model="params.targetDate"></ui-dropdown>
                </s-field>
              </div>
            </div>

            <!-- Progression de conformité par projet -->
            <indicators-progress-bar class="mb-24px" :data="progressStats" v-if="!$apollo.queries.stats.loading" />

            <s-loader :active="$apollo.queries.getIndicators.loading" />
            <div v-if="!$apollo.queries.getIndicators.loading">
              <p v-if="indicatorPage.totalCount === 0">{{ $t('no-results') }}</p>

              <indicators-timeline :indicators="indicatorPage.items" />

              <b-pagination
                v-if="indicatorPage.totalPages > 1"
                :total="indicatorPage.totalCount"
                :per-page="indicatorPage.pageSize"
                :current="indicatorPage.pageNumber"
                @change="(v) => (pageNumber = v)"
              />
            </div>
          </ui-box>
        </div>
      </b-tab-item>
    </b-tabs>
  </div>
</template>

<script>
import isEqual from 'lodash/isEqual'

import EnumHelper from '@/helpers/enums'
import BusinessService from '@/services/business'
import { sendSignal } from '@/services/signals'
import i18n from '@/helpers/i18n'

import Breadcrumbs from '@/mixins/breadcrumbs'
import SField from '@/components/form/field'
import UiBox from '@/components/ui/box'
import UiTitle from '@/components/ui/title'
import UiDropdown from '@/components/ui/dropdown'
import IndicatorSnippet from '@/components/dashboards/indicator-snippet'
import IndicatorsTimeline from '@/components/indicator/indicators-timeline'
import IndicatorsProgressBar from '@/components/projects/indicators-progress-bar.vue'

import {
  BUSINESS_USER_TYPE_BUSINESS_ADMIN,
  BUSINESS_USER_TYPE_BUSINESS_EDITOR,
  BUSINESS_USER_TYPE_GLOBAL_EDITOR,
  BUSINESS_USER_TYPE_PROJECT_EDITOR,
  INDICATOR_TYPE_GENERAL,
  INDICATOR_TYPE_PROJECT,
  INDICATOR_STATUS_TODO,
  INDICATOR_STATUS_IN_PROGRESS,
  INDICATOR_STATUS_DONE,
  INDICATOR_STATUS_NOT_APPLICABLE,
  INDICATOR_TARGET_DATE_UNSET,
  INDICATOR_TARGET_DATE_LATE,
  INDICATOR_TARGET_DATE_UPCOMING
} from '@/consts/enums'

const UNASSIGNED = 'UNASSIGNED',
  TAB_GENERAL = 'general',
  TAB_PROJECTS = 'projects'

export default {
  mixins: [Breadcrumbs],
  components: {
    SField,
    UiBox,
    UiDropdown,
    UiTitle,
    IndicatorsTimeline,
    IndicatorsProgressBar
  },
  data() {
    return {
      UNASSIGNED,
      TAB_GENERAL,
      TAB_PROJECTS,

      indicatorPage: null,
      progressStats: null,
      owners: null,
      projects: null,
      currentUserType: null,

      pageNumber: parseInt(this.$route.query.page) || 1,
      params: {
        selectedTab: this.$route.query.tab || TAB_GENERAL,
        ownerId: this.$route.query.owner || null,
        projectId: this.$route.query.project || null,
        status: this.$route.query.status || null,
        targetDate: this.$route.query.targetDate || null
      }
    }
  },
  computed: {
    breadcrumbs() {
      return [
        {
          label: this.$t('title')
        }
      ]
    },
    statusList() {
      const allStatusItem = {
        icon: 'check-circle',
        label: this.$t('filter-status-placeholder'),
        value: null
      }

      const statuses = [
        INDICATOR_STATUS_TODO,
        INDICATOR_STATUS_IN_PROGRESS,
        INDICATOR_STATUS_DONE,
        INDICATOR_STATUS_NOT_APPLICABLE
      ].map((status) => {
        return {
          label: EnumHelper.formatIndicatorStatus(status),
          value: status
        }
      })

      return [allStatusItem, ...statuses]
    },
    targetDateList() {
      const allTargetDatesItem = {
        icon: 'calendar',
        label: this.$t('filter-target-date-placeholder'),
        value: null
      }

      const targetDates = [INDICATOR_TARGET_DATE_UNSET, INDICATOR_TARGET_DATE_LATE, INDICATOR_TARGET_DATE_UPCOMING].map((x) => {
        return {
          icon: 'calendar',
          label: EnumHelper.formatIndicatorTargetDate(x),
          value: x
        }
      })

      return [allTargetDatesItem, ...targetDates]
    },
    projectList() {
      if (!this.projects && !this.templates) return null

      const allProjectsItem = {
        name: this.$t('filter-project-placeholder'),
        value: null
      }

      const allProjects = this.templates.concat(this.projects)
      const dropdownItems = [allProjectsItem, ...allProjects].map((project) => {
        return {
          icon: 'briefcase',
          label: project.name,
          value: project.id
        }
      })

      return dropdownItems
    },
    ownerList() {
      if (!this.owners) return null

      const allOwnersItem = {
        profile: this.$t('filter-owner-placeholder'),
        id: null
      }

      const unassignedItem = {
        profile: this.$t('filter-owner-unassigned'),
        id: UNASSIGNED
      }

      const getOwnerName = (profile) => {
        if (typeof profile === 'string') {
          return profile
        } else {
          return `${profile.firstName} ${profile.lastName}`
        }
      }

      const dropdownItems = [allOwnersItem, unassignedItem, ...this.owners].map((owner) => {
        return {
          icon: 'user',
          label: getOwnerName(owner.profile),
          value: owner.id
        }
      })

      return dropdownItems
    },
    activeTab: {
      get() {
        if (this.tabs.includes(this.params.selectedTab)) {
          return this.params.selectedTab
        }

        return this.tabs[0]
      },
      set(value) {
        if (!value) return
        this.params.selectedTab = value
      }
    },
    hasAccessToGeneralCompliance() {
      return [BUSINESS_USER_TYPE_GLOBAL_EDITOR, BUSINESS_USER_TYPE_BUSINESS_EDITOR, BUSINESS_USER_TYPE_BUSINESS_ADMIN].includes(
        this.currentUserType
      )
    },
    hasAccessToProjectCompliance() {
      return [BUSINESS_USER_TYPE_GLOBAL_EDITOR, BUSINESS_USER_TYPE_PROJECT_EDITOR, BUSINESS_USER_TYPE_BUSINESS_ADMIN].includes(
        this.currentUserType
      )
    },
    tabs() {
      const tabs = []

      if (this.hasAccessToGeneralCompliance) tabs.push(TAB_GENERAL)
      if (this.hasAccessToProjectCompliance) tabs.push(TAB_PROJECTS)

      return tabs
    },
    ownerFilter() {
      if (this.params.ownerId === null) return null
      if (this.params.ownerId === UNASSIGNED) return { value: null }
      return { value: this.params.ownerId }
    },
    statusFilter() {
      if (this.params.status === null) return { notIn: [INDICATOR_STATUS_DONE, INDICATOR_STATUS_NOT_APPLICABLE] }
      return { eq: this.params.status }
    },
    targetDateFilter() {
      const now = new Date().toISOString().slice(0, 10)
      if (this.params.targetDate === INDICATOR_TARGET_DATE_UNSET) return { null: true }
      if (this.params.targetDate === INDICATOR_TARGET_DATE_LATE) return { lt: now }
      if (this.params.targetDate === INDICATOR_TARGET_DATE_UPCOMING) return { ge: now }

      return null
    },
    queryParameters() {
      const query = {}

      query.tab = this.activeTab
      if (this.params.ownerId) query.owner = this.params.ownerId
      if (this.params.projectId) query.project = this.params.projectId
      if (this.params.status) query.status = this.params.status
      if (this.params.targetDate) query.targetDate = this.params.targetDate
      if (this.pageNumber > 1) query.page = this.pageNumber.toString()

      return query
    },
    dataLoaded() {
      return this.indicatorPage !== null && this.progressStats !== null
    }
  },
  apollo: {
    getData: {
      query() {
        return this.$options.query.GetPendingTasksData
      },
      variables() {
        return {
          businessId: BusinessService.getCurrentBusiness().id,
          type: this.activeTab === TAB_GENERAL ? INDICATOR_TYPE_GENERAL : INDICATOR_TYPE_PROJECT,
          loadProjects: this.activeTab === TAB_PROJECTS
        }
      },
      update({
        searchProjects,
        projectTemplates,
        business: { potentialIndicatorOwners },
        me: {
          userBusiness: [{ userType }]
        }
      }) {
        this.owners = potentialIndicatorOwners
        this.currentUserType = userType

        if (searchProjects) {
          this.projects = searchProjects.items
        }

        if (projectTemplates) {
          this.templates = projectTemplates
        }
      }
    },
    getIndicators: {
      query() {
        return this.$withFragments(
          this.$options.query.GetTasksToDo,
          IndicatorSnippet.fragments.indicatorSnippet,
          IndicatorSnippet.fragments.indicatorSnippetDocuments
        )
      },
      variables() {
        return {
          language: i18n.contentLanguage(),
          businessId: BusinessService.getCurrentBusiness().id,
          type: this.activeTab === TAB_GENERAL ? INDICATOR_TYPE_GENERAL : INDICATOR_TYPE_PROJECT,
          page: this.pageNumber,
          owner: this.ownerFilter,
          project: this.activeTab === TAB_PROJECTS ? this.params.projectId : null,
          status: this.statusFilter,
          targetDate: this.targetDateFilter
        }
      },
      skip() {
        return !this.activeTab
      },
      update({ searchIndicators }) {
        this.indicatorPage = searchIndicators
      },
      fetchPolicy: 'network-only'
    },
    stats: {
      query() {
        return this.$options.query.GetStats
      },
      variables() {
        return {
          language: i18n.contentLanguage(),
          businessId: BusinessService.getCurrentBusiness().id,
          type: this.activeTab === TAB_GENERAL ? INDICATOR_TYPE_GENERAL : INDICATOR_TYPE_PROJECT,
          owner: this.ownerFilter,
          project: this.activeTab === TAB_PROJECTS ? this.params.projectId : null,
          status: this.statusFilter,
          targetDate: this.targetDateFilter
        }
      },
      skip() {
        return !this.activeTab
      },
      update(data) {
        this.progressStats = data.searchIndicatorsStats
      },
      fetchPolicy: 'network-only'
    }
  },
  watch: {
    params: {
      handler() {
        this.pageNumber = 1
      },
      deep: true
    },
    queryParameters(query) {
      if (isEqual(this.$route.query, query)) return
      this.$router.replace({ query })
    },
    dataLoaded() {
      // send signal
      if (this.dataLoaded) {
        this.$nextTick(() => {
          sendSignal('scroll')
        })
      }
    }
  }
}
</script>

<graphql>
query GetPendingTasksData($businessId: ID!, $type: IndicatorType!, $loadProjects: Boolean!) {
  me {
    id
    userBusiness(businessId: { value: $businessId }) {
      id
      userType
    }
  }
  business(id: $businessId) {
    id
    potentialIndicatorOwners(indicatorType: $type) {
      id
      profile {
        id
        firstName
        lastName
      }
    }
  }
  searchProjects(businessId: $businessId, limit: 100, filter: { status: { eq: ACTIVE } }, sort: NAME)
    @include(if: $loadProjects) {
    items {
      id
      name
    }
  }
  projectTemplates(businessId: $businessId) {
    id
    name
  }
}

query GetTasksToDo(
  $language: ContentLanguage!
  $businessId: ID!
  $type: IndicatorType!
  $owner: IDMaybe
  $project: ID
  $status: IndicatorStatusEnumFilter!
  $targetDate: NullableLocalDateFilter
  $page: Int!
) {
  searchIndicators(
    language: $language
    businessId: $businessId
    filter: {
      type: $type
      projectStatus: { notIn: [INACTIVE, FINISH] }
      ownerId: $owner
      projectId: $project
      status: $status
      targetDate: $targetDate
    }
    sort: [{ field: TARGET_DATE, order: ASC }, { field: OWNER_NAME, order: ASC }, { field: PROJECT_NAME, order: ASC }]
    page: $page
    limit: 25
  ) {
    totalCount
    totalPages
    pageSize
    pageNumber
    items {
      id
      language
      targetDate
      ...indicatorSnippet
      ...indicatorSnippetDocuments
    }
  }
}

query GetStats(
  $language: ContentLanguage!
  $businessId: ID!
  $type: IndicatorType!
  $owner: IDMaybe
  $project: ID
  $status: IndicatorStatusEnumFilter!
  $targetDate: NullableLocalDateFilter
) {
  searchIndicatorsStats(
    language: $language
    businessId: $businessId
    filter: {
      type: $type
      projectStatus: { notIn: [INACTIVE, FINISH] }
      ownerId: $owner
      projectId: $project
      status: $status
      targetDate: $targetDate
    }
  ) {
    inProgressCount
    lateCount
    documentsTodoCount
    todoCount
  }
}
</graphql>
